import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { FaPencilAlt, FaTrash } from "react-icons/fa";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import { useQuestions } from "../../contexts/QuestionsContext";
import { Link, useParams } from "react-router-dom";
import axios from "../../axiosConfig";
import ListPageLayout from "../common/ListPageLayout";
import TruncatedText from "../common/TruncatedText";

const QUESTION_MAX_CHARS = 120;

const QuestionsList = () => {
  const { surveyId, pageId } = useParams();
  const { questions, deleteQuestion } = useQuestions();

  const [surveyPage, setSurveyPage] = useState({
    name: "",
    survey_name: "",
    position: "",
  });
  const [loadingHeader, setLoadingHeader] = useState(true);

  const isMobile = useBreakpointValue({ base: true, md: false });

  // ✅ sorting
  const [sortKey, setSortKey] = useState("position"); // question_text | question_type | position
  const [sortDir, setSortDir] = useState("asc"); // asc | desc

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const subTextColor = useColorModeValue("gray.600", "gray.400");

  useEffect(() => {
    const fetchSurveyDetails = async () => {
      try {
        const response = await axios.get(`/surveys/${surveyId}/survey_pages/${pageId}`);
        const { survey_name, page } = response.data;

        setSurveyPage({
          name: page?.name ?? "",
          survey_name: survey_name ?? "",
          position: page?.position ?? "",
        });
      } catch (error) {
        console.error("Error fetching survey details:", error);
      } finally {
        setLoadingHeader(false);
      }
    };

    fetchSurveyDetails();
  }, [surveyId, pageId]);

  const normalize = (v) => (v ?? "").toString().trim().toLowerCase();

  const sortedQuestions = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;

    const valueFor = (q) => {
      switch (sortKey) {
        case "question_text":
          return normalize(q.question_text);
        case "question_type":
          return normalize(q.question_type);
        case "position":
        default:
          return Number(q.position ?? 0);
      }
    };

    return [...(questions ?? [])].sort((a, b) => {
      const av = valueFor(a);
      const bv = valueFor(b);

      if (typeof av === "number" && typeof bv === "number") {
        if (av !== bv) return (av - bv) * dir;
      } else {
        const cmp = av.localeCompare(bv);
        if (cmp !== 0) return cmp * dir;
      }

      // stable fallback: position then id
      const posCmp = Number(a.position ?? 0) - Number(b.position ?? 0);
      if (posCmp !== 0) return posCmp;
      return (a.id ?? 0) - (b.id ?? 0);
    });
  }, [questions, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortIndicator = ({ active }) => {
    if (!active) return null;
    return sortDir === "asc" ? (
      <TriangleUpIcon ml={2} boxSize="10px" />
    ) : (
      <TriangleDownIcon ml={2} boxSize="10px" />
    );
  };

  const renderCustomizationsSummary = (customizations) => {
    if (!customizations || customizations.length === 0) return "None";

    const visibleCount = customizations.filter((c) => c.visible).length;
    const hiddenCount = customizations.filter((c) => !c.visible).length;

    const summary = `${visibleCount} visible, ${hiddenCount} hidden`;

    const details = customizations
      .map((c) => {
        const group = c.survey_group_name || `Group ${c.survey_group_id}`;
        const text = c.custom_question_text ? `"${c.custom_question_text}"` : "Default";
        const hidden = c.visible ? "" : " (Hidden)";
        return `${group}: ${text}${hidden}`;
      })
      .join("; ");

    return (
      <Tooltip label={details} hasArrow placement="top-start" openDelay={250}>
        <Text as="span" cursor="help">
          {summary}
        </Text>
      </Tooltip>
    );
  };

  const renderCard = (q) => (
    <Box
      key={q.id}
      p={4}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      shadow="sm"
      bg={cardBg}
      _hover={{ shadow: "md" }}
    >
      <VStack align="start" spacing={3} w="100%">
        <HStack justify="space-between" w="100%" align="start">
          <Box minW={0} w="100%">
            <TruncatedText
              value={q.question_text}
              maxChars={QUESTION_MAX_CHARS}
              fontWeight="bold"
              fontSize="md"
            />
            <Text fontSize="sm" color={subTextColor}>
              Position: {q.position ?? "—"}
            </Text>
          </Box>

          <Badge variant="subtle" colorScheme="blue" borderRadius="md">
            {q.question_type || "—"}
          </Badge>
        </HStack>

        <Box w="100%" minW={0}>
          <Text fontSize="sm" color={subTextColor} mb={1}>
            Customizations
          </Text>
          <Text>{renderCustomizationsSummary(q.customizations)}</Text>
        </Box>

        <HStack w="100%" justify="flex-end" spacing={2} flexWrap="wrap">
          <Tooltip label="Edit question" hasArrow>
            <IconButton
              icon={<FaPencilAlt />}
              colorScheme="yellow"
              aria-label="Edit Question"
              title="Edit Question"
              variant="outline"
              as={Link}
              size="sm"
              to={`/app/surveys/${surveyId}/pages/${pageId}/questions/edit/${q.id}`}
            />
          </Tooltip>

          <Tooltip label="Delete question" hasArrow>
            <IconButton
              icon={<FaTrash />}
              title="Delete Question"
              aria-label="Delete Question"
              variant="outline"
              colorScheme="red"
              size="sm"
              onClick={() => deleteQuestion(q.id)}
            />
          </Tooltip>
        </HStack>
      </VStack>
    </Box>
  );

  const renderTable = () => (
    <Box
      w="100%"
      maxW="100%"
      minW={0}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      bg={cardBg}
      shadow="sm"
      overflow="hidden"
    >
      <TableContainer w="100%" maxW="100%" minW={0} overflowX="auto">
        <Table
          variant="unstyled"
          size="md"
          tableLayout="fixed"
          w="100%"
          maxW="100%"
          sx={{
            "th, td": { borderBottom: "1px solid", borderColor: "gray.100" },
            "tbody tr:hover": { background: "gray.50" },
          }}
        >
          <colgroup>
            <col style={{ width: "44%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "10%" }} />
          </colgroup>

          <Thead>
            <Tr
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                background: cardBg,
              }}
            >
              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("question_text")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Question text <SortIndicator active={sortKey === "question_text"} />
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("question_type")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                Type <SortIndicator active={sortKey === "question_type"} />
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("position")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
                isNumeric
              >
                Position <SortIndicator active={sortKey === "position"} />
              </Th>

              <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">
                Customizations
              </Th>

              <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">
                Actions
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {sortedQuestions.map((q) => (
              <Tr key={q.id}>
                <Td py={3}>
                  <Box minW={0} w="100%">
                    <TruncatedText
                      value={q.question_text}
                      maxChars={QUESTION_MAX_CHARS}
                      fontWeight="semibold"
                    />
                  </Box>
                </Td>

                <Td py={3}>
                  <Badge variant="subtle" colorScheme="blue" borderRadius="md">
                    {q.question_type || "—"}
                  </Badge>
                </Td>

                <Td py={3} isNumeric fontVariantNumeric="tabular-nums">
                  {q.position ?? "—"}
                </Td>

                <Td py={3}>{renderCustomizationsSummary(q.customizations)}</Td>

                <Td py={3}>
                  <HStack spacing={2} flexWrap="wrap">
                    <Tooltip label="Edit question" hasArrow>
                      <IconButton
                        icon={<FaPencilAlt />}
                        colorScheme="yellow"
                        aria-label="Edit Question"
                        title="Edit Question"
                        variant="outline"
                        as={Link}
                        size="sm"
                        to={`/app/surveys/${surveyId}/pages/${pageId}/questions/edit/${q.id}`}
                      />
                    </Tooltip>

                    <Tooltip label="Delete question" hasArrow>
                      <IconButton
                        icon={<FaTrash />}
                        title="Delete Question"
                        aria-label="Delete Question"
                        variant="outline"
                        colorScheme="red"
                        size="sm"
                        onClick={() => deleteQuestion(q.id)}
                      />
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );

  const title = surveyPage.name
    ? `[${surveyPage.position}] ${surveyPage.name}`
    : "Questions";

  const actions = (
    <HStack spacing={3} flexWrap="wrap">
      {isMobile && (
        <Select
          size="sm"
          w="240px"
          value={`${sortKey}:${sortDir}`}
          onChange={(e) => {
            const [k, d] = e.target.value.split(":");
            setSortKey(k);
            setSortDir(d);
          }}
        >
          <option value="position:asc">Position (Low–High)</option>
          <option value="position:desc">Position (High–Low)</option>
          <option value="question_text:asc">Question (A–Z)</option>
          <option value="question_text:desc">Question (Z–A)</option>
          <option value="question_type:asc">Type (A–Z)</option>
          <option value="question_type:desc">Type (Z–A)</option>
        </Select>
      )}

      <Button
        colorScheme="green"
        as={Link}
        size="sm"
        variant="outline"
        to={`/app/surveys/${surveyId}/pages/${pageId}/questions/create`}
      >
        Add Question
      </Button>
    </HStack>
  );

  return (
    <ListPageLayout title={title} actions={actions} contentProps={{ maxW: "100%", minW: 0 }}>
      {surveyPage.survey_name && (
        <Text fontSize="md" color={subTextColor} mb={1}>
          {surveyPage.survey_name}
        </Text>
      )}

      <Text fontSize="md" color={subTextColor} mb={4}>
        Manage Questions for Page {surveyPage.position || "—"}
      </Text>

      {questions?.length > 0 ? (
        isMobile ? (
          <VStack spacing={4} align="stretch">
            {sortedQuestions.map(renderCard)}
          </VStack>
        ) : (
          renderTable()
        )
      ) : (
        <Text mt={10} textAlign="center">
          No questions found.
        </Text>
      )}
    </ListPageLayout>
  );
};

export default QuestionsList;
